import {Box, Divider, HStack, Text} from '@chakra-ui/react'
import React, {MouseEvent, useEffect, useMemo, useState,} from 'react'
import {useFormContext} from 'react-hook-form'
import Head from 'next/head'
import {isNil, isNumber} from 'lodash-es'
import {useTranslation} from 'next-i18next'
import Image from 'next/legacy/image'
import Select from '_app/components/Form/Select'
import {engraveOptionName} from 'modules/product/utils/engraveOptionName'
import {EngraveTabComponentProps} from '_app/types/product'
import InputWithCounter from '_app/components/Form/InputWithCounter'
import getHTMLInputElementCaretPosition from '_app/utils/getHTMLInputElementCaretPosition'
import {Button} from '_app/components'
import PriceText from '_app/components/PriceText'
import getCurrency from '_app/utils/localized/getCurrency'
import {useRouter} from '_app/hooks/useRouter'
import {FONTS, SPECIAL_CHARACTERS} from './constants'

const MachineWriting = ({
  option,
  onAddOption,
  buttonProps,
}: EngraveTabComponentProps) => {
  const { t } = useTranslation('engraving')
  const { locale } = useRouter()
  const [caretPosition, setCaretPosition] = useState<[number, number]>([0, 0])
  const [chosenFont, setChosenFont] = useState('')
  const [length, setLength] = useState(0)
  const maxCharacters = option?.value?.max_characters || undefined

  const { getValues, setValue } = useFormContext()
  const baseName = useMemo(
    () => `${engraveOptionName(option)}.value`,
    [option]
  )

  useEffect(() => {
    setValue(`${baseName}.options.font`, chosenFont || FONTS?.[0].value)
  }, [baseName, setValue, option, chosenFont])

  const addOptionDisabled =
    !getValues(`${baseName}.value`) ||
    chosenFont === undefined ||
    chosenFont === t('SHOW_ALL')

  const onAddChar = (char: string) => {
    const name = `${baseName}.value`
    const value: string = getValues(name) || ''
    const [start, stop] = caretPosition
    const newValue = `${value?.substring(0, start)}${char}${value?.substring(
      stop,
      value.length
    )}`

    if (isNumber(maxCharacters) && newValue.length > maxCharacters) {
      return
    }

    setCaretPosition([start + char.length, stop + char.length])
    setValue(name, newValue)
  }

  const inputKeyUpHandler = (e: any) => {
    const value = e.target.value
    const regexRule = /[^\p{Letter}\p{Mark}\p{Number}\p{Punctuation}\p{Sc}\p{Sk}\p{Sm}\s♡∞]+/gu
    const position = getHTMLInputElementCaretPosition(e?.nativeEvent?.target as HTMLInputElement)
    if (!position) return
    setLength(value.replace(regexRule, '').substring(0, maxCharacters)?.length)
    setValue(`${baseName}.value`, value.replace(regexRule, ''))
    setCaretPosition(position)
  }

  const inputClickHandler = (e: MouseEvent<HTMLInputElement, globalThis.MouseEvent>) => {
    const position = getHTMLInputElementCaretPosition(e?.nativeEvent?.target as HTMLInputElement)
    if (!position) return
    setCaretPosition(position)
  }

  return (
    <Box minHeight={0}>
      <Head>
        <style
          dangerouslySetInnerHTML={{
            __html:
              "@import url('https://fonts.googleapis.com/css2?family=Nova+Script&display=swap')",
          }}
        />
      </Head>
      {option.value && !isNil(option.value?.price) && (
        <Text
          mb={3}
          mt={{ base: 2, md: 0.5 }}
          fontWeight={300}
          flexDirection="row"
          display="flex"
        >
          {t('MACHINEWRITING_PRICE')}:
          <Text pl={1} fontWeight={400}>
            <PriceText
              amount={{
                value: option.value.price,
                currency: getCurrency(locale),
              }}
              floating
            />
          </Text>
        </Text>
      )}
      <Divider mb={6} />
      <Box>
        <Select
          name={`${baseName}.options.font`}
          label={t('CHOOSE_FONT_LABEL')}
          placeholder={t('CHOOSE_FONT_PLACEHOLDER')}
          options={FONTS.map(font => ({
            label: (
              <Image
                alt=""
                src={font.label}
                height="32"
                width="143"
                layout="fixed"
              />
            ),
            value: font.value,
          }))}
          defaultValue={FONTS?.[0].value}
          setActive={() => setChosenFont(getValues(`${baseName}.options.font`))}
          isImages
        />
      </Box>
      <Box
        mt={6}
        mb={2}
        display="flex"
        flexDirection={{ md: 'row', base: 'column' }}
        justifyContent="space-between"
        gap={{ md: 6, base: 1 }}
        alignItems="center"
      >
        <InputWithCounter
          name={`${baseName}.value`}
          label={t('CONTENT_LABEL')}
          placeholder={t('CONTENT_PLACEHOLDER')}
          maxCharacters={maxCharacters}
          onKeyUp={inputKeyUpHandler}
          onClick={inputClickHandler}
          length={length}
          disablePasting
        />
        <Button
          onClick={onAddOption}
          type="button"
          disabled={addOptionDisabled}
          width="100%"
          {...buttonProps}
        />
      </Box>

      <HStack spacing="0.375rem" alignItems="center" pb={4} sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
        <Box sx={{ display: 'inline-flex' }}>
          <Text fontWeight={300}>{t('INSERT_SPECIAL_CHARACTER')}</Text>
          {SPECIAL_CHARACTERS.map(char => (
              <Text
                  key={`spc-${char}`}
                  fontSize="1.2em"
                  role="button"
                  onClick={() => onAddChar(char)}
              >
                {char}
              </Text>
          ))}
        </Box>
        {
            getValues(`${baseName}.value`)?.length > 0 && (
                <span style={{ color: 'red' }}>{t('ERROR_TO_ACCEPT')}</span>
            )
        }
      </HStack>
    </Box>
  )
}

export default MachineWriting
